import classNames from 'classnames';
import { Formik } from 'formik';
import React, { useRef, useState, useEffect, useCallback, ChangeEvent } from 'react';
import * as Yup from 'yup';
import { getMakeModel, getVehicleMakes, getVehicleModels, getVehicleYears, IMakeModel } from '../../../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist, useTowInProcess } from '../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../../context/Page/page';
import { Accordion } from '../../../../controls/Accordion/Accordion/Accordion';
import { DynamicAccordionSection } from '../../../../controls/Accordion/DynamicAccordionSection';
import { GMAppointmentName } from '../../../../controls/AppointmentName';
import { CTAButton } from '../../../../controls/ButtonBase';
import { Heading } from '../../../../controls/Heading/Heading';
import { DisplayMake, DisplayModel } from '../../../../helpers/displayVehicle';
import css from './Vehicle.module.scss';
import { Spinner } from './../../../../controls/Spinner/Spinner';
import { nameof } from '../../../../helpers/nameof';
import { KeyboardLayouts } from '../../../../controls/SimpleKeyboard/Layout';
import { useKeyboardDispatch, KeyboardDispatchTypes } from '../../../../context/Keyboard/keyboard';
import { IconButton } from '../../../../controls/IconButton/IconButton';
import { isElectron } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { useNavigate } from 'react-router-dom';
import { AppRoutePaths, Customer_AddRoutesPaths, CustomerRoutePaths, PathName } from '../../../../helpers/Routes';
import { GMFormVinField } from '../../../../controls/TextField';
import { GMBox } from '../../../../controls/Box';
import { GMSizedIcon } from '../../../../controls/Icon';
import { useScrollTo } from '../../../../helpers/useScrollTo';
import { GMForm, GMFormWrapper } from '../../../../controls/Form';

export interface IProps {
    onProceed: () => void;
    /** Whether the selections should display in a wizard style flow */
    asWizard?: boolean;
}

export const Vehicle: React.FC<IProps> = React.memo((props) => {
    const {
        key: kioskKey,
        dealer: { key: dealerKey, make, country },
    } = useKioskState();
    const towInProcess = useTowInProcess();
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const Localization = useLocalization();
    const currentAccordionRef = useRef(null);
    const [matchedMakes, setMatchedMakes] = React.useState<string[]>([]);
    const [numModels, setNumModels] = React.useState<number>(0);
    const dealerMakeList = make?.split(',')
        .map((make) => make.trim());
    const [loading, setLoading] = useState<boolean>(true);
    const [dataCleansed, setDataCleansed] = useState<boolean>(false);
    const keyboardDispatch = useKeyboardDispatch();
    const [loadingVinAutofill, setLoadingVinAutofill] = React.useState(false);
    const isKiosk = isElectron();
    const navigate = useNavigate();
    const [badVin, setBadVin] = useState<string>();
    const hideKioskBackground = useHideKioskBackground();

    useScrollTo({ scrollable: currentAccordionRef });

    type InitialValues = Pick<typeof appointment, 'year' | 'make' | 'model' | 'vin'>;

    const [initialValues, setInitialValues] = useState<InitialValues>({
        year: appointment.year ?? '',
        make: DisplayMake(appointment),
        model: DisplayModel(appointment),
        vin: appointment.vin,
    });

    useEffect(() => {
        hideKioskBackground();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * Preserve make/model/series/style in the case that only the year is missing, then we can validate these values
     * when a valid year is provided, otherwise disregard these fields and prompt the customer for each as usual
     */
    const [valuesWithYearMissing, setValuesWithYearMissing] = useState<Omit<InitialValues, 'year' | 'vin'> | undefined>(
        !appointment.year && !!DisplayMake(appointment)
            ? {
                make: DisplayMake(appointment),
                model: DisplayModel(appointment),
            }
            : undefined,
    );

    const getOrderedandMatchedMakes = async (year: string) => {
        const makes = await getVehicleMakes(kioskKey, dealerKey, country, year) ?? [];
        const ejectedMakes = [];

        if (dealerMakeList) {
            for (let i = 0; i < dealerMakeList.length; i++) {
                const currentMake = dealerMakeList[i];

                // check if DisplayMake(appointment) is in the list
                const apptMakeIndex = makes.findIndex((make) => make.toLowerCase() === DisplayMake(appointment)
                    .toLowerCase());

                const dealerMakeIndex = makes.indexOf(currentMake);

                // if the DisplayMake(appointment) is in the list give that priority
                if (apptMakeIndex > -1) {
                    ejectedMakes.push(makes[apptMakeIndex]);
                    makes.splice(apptMakeIndex, 1);
                }
                else if (dealerMakeIndex > -1) {
                    makes.splice(dealerMakeIndex, 1);
                    ejectedMakes.push(currentMake);
                }
            }

            makes.splice(0, 0, ...ejectedMakes);
            setMatchedMakes(ejectedMakes);
        }

        return {
            orderedMakes: makes,
            matchedMakes: ejectedMakes,
        };
    };

    const getMakeModelFromVin = useCallback((vin: string) => {
        if (!loadingVinAutofill && badVin !== vin.toLocaleUpperCase()) {
            setLoadingVinAutofill(true);
            getMakeModel(vin)
                .then((makeModel: IMakeModel) => {
                    const currentYear = new Date(Date.now())
                        .getFullYear();
                    let foundYear;

                    // this should be good for a decade and a half, by then we'll probably all be riding in vtols or something
                    if (vin[9] >= 'A' && vin[9] <= 'Y') {
                        let adjustment = 0;
                        if (vin[9] >= 'A' && vin[9] <= 'H') {
                            adjustment = 65;
                        }
                        else if (vin[9] >= 'J' && vin[9] <= 'N') {
                            adjustment = 66;
                        }
                        else if (vin[9] === 'P') {
                            adjustment = 67;
                        }
                        else if (vin[9] >= 'R' && vin[9] <= 'T') {
                            adjustment = 68;
                        }
                        else if (vin[9] >= 'V' && vin[9] <= 'Y') {
                            adjustment = 69;
                        }

                        if (adjustment !== 0) {
                            foundYear = (vin.charCodeAt(9)) - adjustment;
                            foundYear += (currentYear - (foundYear + 1980) > 28) ? 2010 : 1980;
                        }
                    }
                    else if (vin[9] >= '1' && vin[9] <= '9') {
                        foundYear = (vin.charCodeAt(9)) - 49;
                        foundYear += (currentYear - (foundYear + 2001) > 28) ? 2031 : 2001;
                    }

                    if (foundYear) {
                        if ((makeModel?.model ?? '') === '') {
                            setBadVin(vin.toLocaleUpperCase());
                        }

                        setInitialValues({
                            year: foundYear.toString(),
                            make: (makeModel.make ?? ''),
                            model: (makeModel.model ?? ''),
                            vin: vin,
                        });
                    }
                })
                .finally(() => {
                    setLoadingVinAutofill(false);
                })
                .catch(() => {
                    setBadVin(vin.toLocaleUpperCase());
                    setLoadingVinAutofill(false);
                });
        }
    }, [badVin, loadingVinAutofill]);

    useEffect(() => {
        if (!isKiosk && initialValues.vin?.length === 17 && (initialValues.make ?? '') === '' && (initialValues.model ?? '') === '') {
            getMakeModelFromVin(initialValues.vin);
        }
    }, [initialValues.make, initialValues.model, initialValues.vin, getMakeModelFromVin, isKiosk]);

    useEffect(() => {
        // grab the valid makes list and see if DisplayMake(appointment) is in that
        const checkAppointmentMakeForMatch = async function (apptYear: string, apptMake: string, vin: string) {
            setDataCleansed(true);

            if (!dataCleansed) {
                if (apptYear !== '') {
                    const makesList = await getVehicleMakes(kioskKey, dealerKey, country, apptYear) ?? [];

                    // check if DisplayMake(appointment) is in the list
                    const apptMakeIndex = makesList.findIndex((make) => make.toLowerCase() === apptMake?.toLowerCase());

                    /*
                     * if DisplayMake(appointment) is in the list, auto-assign that to our initialValues make
                     * sanity check for series and style to check for null
                     */
                    if (apptMakeIndex > -1) {
                        setInitialValues({
                            year: apptYear,
                            make: makesList[apptMakeIndex],
                            model: DisplayModel(appointment),
                            vin,
                        });
                    }
                    else {
                        // make isn't valid, which means the model/series/style are also not valid for our new make
                        setInitialValues({
                            year: apptYear,
                            make: '',
                            model: '',
                            vin,
                        });
                    }
                }
            }

            setLoading(false);
        };
        checkAppointmentMakeForMatch(appointment.year ?? '', DisplayMake(appointment), appointment.vin);
    }, [appointment, initialValues, kioskKey, dealerKey, country, dataCleansed]);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) =>
                setAppointment(
                    {
                        ...appointment,
                        ...values,
                        // reset the chromeMake and chromeModel if they were altered
                        chromeMake: [appointment.make?.toLowerCase(), appointment.chromeMake?.toLowerCase()].includes(values.make!.toLowerCase())
                            ? appointment.chromeMake ?? values.make
                            : values.make,
                        chromeModel: [appointment.model?.toLowerCase(), appointment.chromeModel?.toLowerCase()].includes(values.model!.toLowerCase())
                            ? appointment.chromeModel ?? values.model
                            : values.model,
                    },
                    true,
                )
                    .then(props.onProceed)
            }
            validationSchema={Yup.object()
                .shape({
                    [nameof<typeof initialValues>('year')]: Yup.string()
                        .nullable()
                        .required(),
                    [nameof<typeof initialValues>('make')]: Yup.string()
                        .nullable()
                        .required(),
                    [nameof<typeof initialValues>('model')]: Yup.string()
                        .nullable()
                        .required(),
                })}
            enableReinitialize
        >
            {({ values, setValues, touched, errors, submitForm, setFieldValue, dirty }) => {
                const clearData = (data: keyof typeof values) => {
                    switch (data) {
                        case nameof<typeof values>('year'):
                            setValues({
                                ...values,
                                year: '',
                                make: '',
                                model: '',
                            });
                            break;
                        case nameof<typeof values>('make'):
                            setValues({
                                ...values,
                                make: '',
                                model: '',
                            });
                            break;
                        case nameof<typeof values>('model'):
                            setValues({
                                ...values,
                                model: '',
                            });
                            break;
                    }
                };

                const allSectionsValid = !props.asWizard || !!(values.year && values.make && values.model);

                const goBack = () => {
                    if (values.model && values.make && values.year) {
                        clearData('model');
                    }
                    else if (values.make && values.year) {
                        clearData('make');
                    }
                    else if (values.year) {
                        clearData('year');
                    }
                };

                const yearExpanded = !values.year;
                const makeExpanded = !yearExpanded && !values.make;
                const modelExpanded = !!values.year && !!values.make && !values.model;

                // if year was missing, reinitialize values once we get a valid year
                if (!!values.year && valuesWithYearMissing) {
                    setAppointment({
                        ...appointment,
                        ...valuesWithYearMissing,
                        year: values.year,
                    }, true)
                        .then(() => {
                            setValuesWithYearMissing(undefined);
                            setDataCleansed(false);
                        });
                }

                return loading ? (
                    <Spinner />
                ) : (
                    <GMForm
                        heading={
                            <Heading
                                primaryHeading={towInProcess ? Localization.CustomersVehicle : Localization.formatString(Localization.HiCustomer, <GMAppointmentName />)}
                                secondaryHeading={towInProcess ? Localization.PleaseEnterTheVehiclesInformationBelow : Localization.PleaseEnterVehicleInformation}
                                leftAlign
                            />
                        }
                        submitButton={
                            (allSectionsValid && (
                                <CTAButton
                                    styleType={'primary'}
                                    type={'submit'}
                                    title={Localization.ClickToProceedToNextStep}
                                    label={Localization.Proceed}
                                    onClick={submitForm}
                                    id={Localization.ClickToProceedToNextStep}
                                    ariaLabel={Localization.ClickToProceedToNextStep}
                                />
                            )) ||
                            undefined
                        }
                        backButton={
                            props.asWizard && !!values.year ? (
                                <CTAButton
                                    styleType={'secondary'}
                                    type={'button'}
                                    label={Localization.Previous}
                                    onClick={goBack}
                                    kioskOptions={{
                                        inlineOptions: {
                                            leftIcon: true,
                                            sizedIcon: (
                                                <GMSizedIcon
                                                    iconSize={'medium'}
                                                    iconType={'Arrow-Back'}
                                                    ariaLabel={Localization.BackArrow}
                                                />
                                            ),
                                        },
                                    }}
                                    id={Localization.Previous}
                                    ariaLabel={Localization.Previous}
                                />
                            ) : undefined
                        }
                        fullWidthKioskInputs={props.asWizard}
                        hideKeyboard={!(towInProcess)}
                        topDivider={props.asWizard}
                    >
                        <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                        {
                            towInProcess &&
                            <div className={css.vinFieldContainer}>
                                <div className={css.vinField}>
                                    <GMFormVinField
                                        name={nameof<typeof values>('vin')}
                                        id={nameof<typeof values>('vin')}
                                        title={Localization.VINFullOrLast8}
                                        label={Localization.VINFullOrLast8}
                                        ariaLabel={Localization.VINFullOrLast8}
                                        vinType={'vinFullOrLast8'}
                                        disabled={loadingVinAutofill}
                                        keyboardLayout={KeyboardLayouts.AlphanumericNumpad}
                                        onChangeCallback={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                            if (event.target.value.length === 17 && dirty) {
                                                getMakeModelFromVin(event.target.value.toUpperCase());
                                            }
                                        }}
                                        capsLock
                                        autoFocus
                                    />
                                </div>
                                {
                                    !isKiosk &&
                                    <div className={css.barcodeButton}>
                                        <IconButton
                                            slim
                                            onClick={() => navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Add, Customer_AddRoutesPaths.VinScanMobile))}
                                            sizedIcon={(
                                                <div className={css.barcodeImg}>
                                                    <GMSizedIcon iconType={'Barcode-Line'} iconSize={'custom'} />
                                                </div>
                                            )}
                                            title={Localization.ClickToStartScan}
                                            ariaLabel={Localization.ClickToStartScan}
                                            id={Localization.ClickToStartScan}
                                        />
                                    </div>
                                }
                            </div>
                        }
                        <div className={classNames({ [css.asWizard]: props.asWizard })}>
                            <Accordion>
                                {(yearExpanded || allSectionsValid) && (
                                    <DynamicAccordionSection
                                        name={nameof<typeof values>('year')}
                                        key={'Year'}
                                        expanded={yearExpanded}
                                        label={Localization.Year + '\u00A0*'}
                                        error={!!touched.year && !!errors.year}
                                        onClear={() => clearData('year')}
                                        getValues={async () => (await getVehicleYears(country))?.map((year) => ({
                                            label: '' + year,
                                            value: '' + year,
                                        })) ?? []}
                                        parentLoading={loadingVinAutofill}
                                    />
                                )}
                                {(makeExpanded || allSectionsValid) && (
                                    <DynamicAccordionSection
                                        name={nameof<typeof values>('make')}
                                        expanded={makeExpanded}
                                        label={Localization.Make + '\u00A0*'}
                                        error={!!touched.make && !!errors.make}
                                        onClear={() => clearData('make')}
                                        getValues={async () => {
                                            keyboardDispatch({
                                                type: KeyboardDispatchTypes.KeyboardHidden,
                                                payload: true,
                                            });

                                            const makes = await getOrderedandMatchedMakes(values.year!);
                                            if (!props.asWizard) {
                                                if (makes && makes.matchedMakes && makes.matchedMakes.length === 1) {
                                                    setFieldValue(nameof<typeof values>('make'), makes.matchedMakes[0]);
                                                }
                                            }

                                            return makes?.orderedMakes?.map((make) => ({
                                                label: make,
                                                value: make,
                                            }));
                                        }}
                                        preserveCase
                                        key={values.year!}
                                        scrollTo={!(matchedMakes && matchedMakes.length === 1)}
                                        maxFields={matchedMakes && matchedMakes.length >= 1 ? matchedMakes.length : undefined}
                                        parentLoading={loadingVinAutofill}
                                    />
                                )}
                                {(modelExpanded || allSectionsValid) && (
                                    <DynamicAccordionSection
                                        name={nameof<typeof values>('model')}
                                        expanded={modelExpanded}
                                        label={Localization.Model + '\u00A0*'}
                                        error={!!touched.model && !!errors.model}
                                        onClear={() => clearData('model')}
                                        getValues={async () => {
                                            keyboardDispatch({
                                                type: KeyboardDispatchTypes.KeyboardHidden,
                                                payload: true,
                                            });

                                            const models = await getVehicleModels(kioskKey, dealerKey, country, values.year!, values.make!) ?? [];
                                            setNumModels(models.length);
                                            if (models.length === 1) {
                                                setFieldValue(nameof<typeof values>('model'), models[0]);
                                            }

                                            return models.map((model) => ({
                                                label: model,
                                                value: model,
                                            }));
                                        }}
                                        preserveCase
                                        key={[values.year, values.make].join()}
                                        scrollTo={true}
                                        maxFields={numModels}
                                        parentLoading={loadingVinAutofill}
                                    />
                                )}
                            </Accordion>
                            {props.asWizard && (
                                <div>
                                    <div className={css.selectedVehicleContainer}>
                                        <GMBox color={'primary'}>
                                            <div className={css.selectedVehicleContent}>
                                                <div className={css.selectedVehicleText}>
                                                    {values.year ? Localization.YourSelectedVehicle + ':' : Localization.YourSelectedVehicleWillDisplayHere}
                                                </div>
                                                {values.year && (
                                                    <GMBox color={'tertiary'}>
                                                        <div className={css.selectedVehicle}>
                                                            <div className={classNames(css.selecteVehicleMakeModel, css.selectVehicleValue)}>
                                                                {values.make} {values.model}
                                                            </div>
                                                            <div className={css.selectVehicleValue}>{values.year}</div>
                                                        </div>
                                                    </GMBox>
                                                )}
                                            </div>
                                        </GMBox>
                                    </div>
                                    <div className={css.selectVehicleHint}>{Localization.ChangeYourVehicleSectionHint}</div>
                                </div>
                            )}
                        </div>
                    </GMForm>
                );
            }}
        </Formik>
    );
});
